import { Type } from 'class-transformer';
import { differenceInDays } from 'date-fns';
import { Nullable } from '@cast/leadcast-types';
import { CASTFile } from '../CASTFile';

export interface SampleResult {
  id: string;
  externalId: string;
  collectedDate: Date;
  analyzedDate: Date;
  resultsAvailableAtLabDate: Date;
  result: ResultDetails;
  propertyAddress: string;
  propertyId: string;
  monitoringProgram: {
    id: string;
    name: string;
  };
  report: {
    externalId: string;
    pdfReportLink: string;
    pdfReport: CASTFile;
    analyticalReports: CASTFile[];
  };
}

export class SampleResult {
  @Type(() => Date)
  collectedDate: Date;

  @Type(() => Date)
  analyzedDate: Date;

  @Type(() => Date)
  resultsAvailableAtLabDate: Date;

  get processingTime(): Nullable<number> {
    if (!this.analyzedDate || !this.collectedDate) {
      return null;
    }
    return differenceInDays(this.analyzedDate, this.collectedDate);
  }

  get resultDisplay() {
    if (!!this.result.value || this.result.value === 0) {
      return `${this.result.value} ${this.result.units}`;
    } else {
      return '';
    }
  }

  get standardizedValue() {
    return standardizeResultValueToPpb(this.result.value, this.result.units);
  }

  getResultStatus(threshold?: number, leadMdlRl?: number): ResultStatus {
    const value = this.standardizedValue;
    return getResultStatus(value, threshold, leadMdlRl);
  }
}

export function standardizeResultValueToPpb(value: string | number, units?: string | null): number {
  const numVal = Number(value);
  if (typeof numVal !== 'number') {
    return 0;
  }
  const convertToPpb = {
    ppt: 1000000,
    ppm: 1000,
    ppb: 1,
    undefined: 0,
    null: 0,
  };

  return numVal * convertToPpb[units?.toLowerCase()];
}

export function getResultStatus(
  standardizedValue: number,
  threshold?: number,
  leadMdlRl?: number
): ResultStatus {
  if (!threshold) {
    if (standardizedValue > 0) {
      return 'Detected';
    } else {
      return 'No Detection';
    }
  }

  if (standardizedValue > threshold) {
    return 'Exceedance';
  } else if (standardizedValue < threshold && standardizedValue >= leadMdlRl) {
    return 'Detected';
  } else if (standardizedValue < leadMdlRl) {
    return 'No Detection';
  }
}

export type ResultStatus = 'Exceedance' | 'Detected' | 'No Detection';

interface ResultDetails {
  value: Nullable<string | number>;
  type: string;
  status: string;
  units: Nullable<'ppm' | 'PPM' | 'ppb' | 'PPB'>;
  notes: string;
  labQualifier: string;
  validationQualifier: string;
  finalQualifier: string;
  finalQualifierNotes: string;
  methodDetectionLimit: string;
  reportingDetectionLimit: string;
  quantationDetectionLimit: string;
  isValidated: boolean;
  createdOn: Date;
  createdBy: Date;
  dilutionFactor: string;
  fraction: number;
  methodName: string;
  method: string;
  lab: string;
  labSampleGroup: string;
  labSampleGroupId: string;
  analyte: {
    name: string;
    standardUnits: string;
  };
}
